import React from "react";
import { Modal, Steps, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { pushNotification } from "../compliance/ux/notificationStore";
import { runInvestorDemo, type InvestorDemoStep } from "./runInvestorDemo";

type Props = {
  open: boolean;
  onClose: () => void;
  onFinished?: () => void;
};

const STEP_ORDER = ["create", "documents", "validate", "passport"] as const;

/** US-401: narration UI for the scripted investor demo. */
export function InvestorDemoModal({ open, onClose, onFinished }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [steps, setSteps] = React.useState<InvestorDemoStep[]>([]);
  const [running, setRunning] = React.useState(false);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setSteps([]);
      setRunning(false);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    setRunning(true);
    void (async () => {
      try {
        const result = await runInvestorDemo(setSteps);
        message.success(t("investor.demo.success"));
        pushNotification({
          kind: "passport_generated",
          title: t("investor.demo.success"),
          caseId: result.caseDetail.id,
        });
        onFinished?.();
        onClose();
        navigate(`/dds/cases/${result.caseDetail.id}`);
      } catch {
        message.error(t("investor.demo.failed"));
        setRunning(false);
      }
    })();
  }, [open, navigate, onClose, onFinished, t]);

  const current = Math.max(
    0,
    steps.findIndex((s) => s.status === "running" || s.status === "error")
  );
  const statusMap = Object.fromEntries(steps.map((s) => [s.id, s.status]));

  return (
    <Modal
      open={open}
      title={t("investor.demo.title")}
      footer={null}
      closable={!running}
      maskClosable={!running}
      onCancel={onClose}
      data-testid="investor-demo-modal"
    >
      <Typography.Paragraph type="secondary">{t("investor.demo.lead")}</Typography.Paragraph>
      <Steps
        direction="vertical"
        size="small"
        current={current >= 0 ? current : STEP_ORDER.length - 1}
        items={STEP_ORDER.map((id) => ({
          title: t(`investor.demo.steps.${id}`),
          description: t(`investor.demo.narration.${id}`),
          status:
            statusMap[id] === "done"
              ? "finish"
              : statusMap[id] === "error"
                ? "error"
                : statusMap[id] === "running"
                  ? "process"
                  : "wait",
        }))}
      />
    </Modal>
  );
}
